<script setup>

// Store dependencies
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";

// Components
import AdminSidebar from '@/components/layout/AdminSidebar.vue'
import Navbar from '@/components/layout/Navbar.vue'
import SharedSpinner from '@/components/shared/SharedSpinner.vue'
import ImpersonationBanner from '@/components/layout/ImpersonationBanner.vue'
import UpdateBanner from '@/components/layout/UpdateBanner.vue'


// Authentication
import auth from '@/api/auth/auth'
import { computed, onMounted, onUpdated, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const uiStore = useUiStore();
const authenticated = ref(true);
const openSidebar = ref(true);
const showSide = ref(false)

const userLogged = computed(() => authStore.user);
const loading = computed(() => uiStore.loading);

const PUBLIC_ROUTE_NAMES = ['login', 'recover-password', 'confirm-password-reset'];

watch(
  route, (to, from) => {
    if (from.name === 'login' && !PUBLIC_ROUTE_NAMES.includes(to.name)) {
      initDataApplication();
    }
  });

onMounted(() => {
  uiStore.activeRequests = 0;
  uiStore.stopLoading();
  authenticated.value = auth.checkAuth();
  const storage = localStorage.getItem('sidebarState');
  if (storage != null) {
    openSidebar.value = JSON.parse(storage);
  }
  initDataApplication();
})
onUpdated(() => {
  authenticated.value = auth.checkAuth();
})

const initDataApplication = () => {
  if (PUBLIC_ROUTE_NAMES.includes(route.name)) {
    return;
  }
  authStore.initialiseStore();
  if (!authStore.getUserLogged) {
    auth.logout();
  }
}
// eslint-disable-next-line no-unused-vars
const showSidebar = () => {
  openSidebar.value = !openSidebar.value
  localStorage.setItem('sidebarState', JSON.stringify(openSidebar.value));
}

const toggleSidebar = () => {
  showSide.value = !showSide.value;
}

const redirectToTrackernet = () => {
  router.push({ name: 'tracker' })
}

const openLinkInNewTab = (link) => {
  window.open(link, '_blank');
}

</script>

<template>
  <div>
    <div :class="{ 'loading': loading }">
      <!--      <div id="grad" v-if="userLogged && authenticated"></div>-->
      <main class="main-content"
        :class="{ 'opened': authenticated && openSidebar, 'full-width': !authenticated, 'closed': authenticated && !openSidebar, 'openSide': showSide, closSide: !showSide }">
        <SharedSpinner v-if="loading" />
        <div style="margin-right: 7px;">
          <router-view />
        </div>
      </main>
      <div v-if="showSide" class="overlay-diffuse" @click="toggleSidebar"></div>
      <div id="footer" :class="{ 'openSide': showSide }" v-if="authenticated">
        <div class="fixed-footer d-flex justify-content-center align-items-center"
          style="box-shadow: 4px 0px 4px 0px #00000040;">
          <div class="footer-icon">
            <a @click="redirectToTrackernet()">
              <!--          <a href="javascript:void(0)" @click="openLinkInNewTab('https://app.clockify.me/')">-->
              <!--          <a href="https://app.clockify.me/" target="_blank" rel="noreferrer noopener">-->
              <el-card class="box-card d-flex justify-content-center pointer">
                <svg id="trackernet-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 66" fill="none">
                  <g clip-path="url(#clip0_95_196)">
                    <path
                      d="M38.7428 0.811768H25.5044C24.8354 0.811768 24.2949 1.35229 24.2949 2.02123V7.2264C24.2949 7.89534 24.8354 8.43586 25.5044 8.43586H29.1806V11.6999C29.1806 12.1927 29.5777 12.5898 30.0705 12.5898H34.4544C34.9472 12.5898 35.3443 12.1927 35.3443 11.6999V8.43586H38.7458C39.4147 8.43586 39.9552 7.89534 39.9552 7.2264V2.02123C39.9552 1.35229 39.4147 0.811768 38.7458 0.811768H38.7428Z"
                      fill="#474CE6" />
                    <path
                      d="M14.2974 10.7128L9.15855 13.6797C8.70005 13.9444 8.54296 14.5307 8.80768 14.9892L10.1172 17.2573C10.3819 17.7158 10.9682 17.8729 11.4267 17.6082L16.5655 14.6413C17.024 14.3766 17.1811 13.7903 16.9164 13.3318L15.6069 11.0637C15.3422 10.6052 14.7559 10.4481 14.2974 10.7128Z"
                      fill="#474CE6" />
                    <path
                      d="M47.6838 14.6431L52.8226 17.61C53.2811 17.8747 53.8674 17.7176 54.1321 17.2591L55.4416 14.991C55.7064 14.5325 55.5493 13.9462 55.0908 13.6815L49.9519 10.7146C49.4934 10.4499 48.9071 10.607 48.6424 11.0655L47.3329 13.3336C47.0682 13.7921 47.2253 14.3784 47.6838 14.6431Z"
                      fill="#474CE6" />
                    <path
                      d="M43.6404 62.8646C43.6404 61.0609 43.6464 59.1885 43.6643 57.3698C50.2342 57.3788 43.4731 57.3698 49.9923 57.3608C49.9923 59.1646 50.0042 61.0549 50.0013 62.8228C43.4821 62.8318 50.2432 62.8408 43.6404 62.8676V62.8646Z"
                      fill="#474CE6" />
                    <path
                      d="M35.2159 49.3724C33.4808 49.3724 31.6622 49.3933 29.9988 49.3963C30.0077 42.704 30.0346 35.9937 30.0257 29.4596C31.6204 29.4596 33.5107 29.4387 35.3652 29.4178C35.3204 35.9369 35.2607 42.6472 35.2159 49.3724Z"
                      fill="#474CE6" />
                    <path
                      d="M35.9356 56.8263C37.9603 58.8511 37.9394 62.0584 35.8908 64.0742C33.9109 66.0183 30.7035 66.0063 28.7296 64.0652C26.7228 62.0584 26.6899 58.7973 28.6878 56.8024C30.6319 54.7897 33.9437 54.8016 35.9356 56.8263Z"
                      fill="#474CE6" />
                    <path
                      d="M32.1578 14.5459C14.7863 14.5459 0.655029 28.6772 0.655029 46.0487C0.655029 49.9249 1.3598 53.6399 2.64691 57.0712C2.70664 57.2803 2.78727 57.4833 2.87984 57.6744C3.50996 58.9795 4.83888 59.8813 6.38579 59.8813C8.53595 59.8813 10.28 58.1373 10.28 55.9872C10.28 55.4735 10.1784 54.9868 9.99925 54.5388C9.98731 54.5089 9.97536 54.4791 9.96341 54.4492C8.96897 51.8362 8.42247 49.0021 8.42247 46.0457C8.42247 32.9566 19.0717 22.3074 32.1608 22.3074C45.2499 22.3074 55.8991 32.9566 55.8991 46.0457C55.8991 48.9633 55.3675 51.7585 54.4 54.3417C54.3283 54.497 54.2596 54.6583 54.2089 54.8255C54.0954 55.1898 54.0356 55.581 54.0356 55.9812C54.0356 58.1343 55.7797 59.8784 57.9328 59.8784C59.4648 59.8784 60.7848 58.9944 61.4238 57.7073C61.5283 57.4953 61.6179 57.2713 61.6836 57.0384C62.9648 53.613 63.6636 49.91 63.6636 46.0427C63.6636 28.6712 49.5323 14.5399 32.1608 14.5399L32.1578 14.5459Z"
                      fill="#474CE6" />
                  </g>
                </svg>
                <h2 id="trackernet-title">Trackernet</h2>
              </el-card>
            </a>
          </div>
          <div class="footer-icon">
            <a :href="'https://www.outlook.com/'" target="_blank" @click="openLinkInNewTab('https://www.outlook.com/')">
              <!--          <a href="https://gmail.com/" target="_blank" rel="noreferrer noopener">-->
              <el-card class="box-card d-flex justify-content-center pointer">
                <svg id="mail-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66 66">
                  <path xmlns="http://www.w3.org/2000/svg"
                    d="M33.0798 0.811829C32.7277 0.811829 32.3486 0.865995 31.9965 0.920162L4.99442 6.33683C2.4215 6.82433 0.579834 9.04516 0.579834 11.6452V54.9785C0.579834 57.5785 2.4215 59.8264 4.99442 60.2868L31.9965 65.6764C32.3486 65.7577 32.7277 65.7847 33.0798 65.7847C36.059 65.7847 38.4965 63.3472 38.4965 60.3681V6.2285C38.4965 3.24933 36.059 0.811829 33.0798 0.811829ZM43.9132 6.2285V17.9827L49.3298 21.8556L65.4173 10.3722C64.8486 7.98891 62.709 6.2285 60.1632 6.2285H43.9132ZM65.5798 16.8993L50.9007 27.3806C50.4403 27.7056 49.8715 27.8952 49.3298 27.8952C48.7882 27.8952 48.2194 27.7327 47.759 27.3806L43.9132 24.6452V42.3577C46.6215 38.5118 51.0632 36.0202 56.1007 36.0202C59.7027 36.0202 63.0069 37.2931 65.5798 39.4327V16.8993ZM18.4007 19.7702C21.1632 19.7702 23.4111 20.9889 25.0903 23.4535C26.7965 25.891 27.6361 29.141 27.6361 33.1222C27.6361 37.1035 26.7423 40.5431 24.9819 43.0618C23.2215 45.5806 20.8923 46.8535 18.0757 46.8535C15.259 46.8535 13.0382 45.6347 11.3048 43.1972C9.5715 40.7597 8.67775 37.5639 8.67775 33.6639C8.67775 29.5202 9.5715 26.1889 11.3319 23.616C13.1194 21.0702 15.4486 19.7702 18.3736 19.7702H18.4007ZM18.2653 25.1868C16.9923 25.1868 15.9902 25.9452 15.2319 27.4347C14.4736 28.9243 14.1215 30.9014 14.1215 33.366C14.1215 35.8306 14.5007 37.8347 15.2319 39.2702C15.9902 40.7056 16.9653 41.4368 18.1569 41.4368C19.3486 41.4368 20.4048 40.7327 21.1361 39.3243C21.8673 37.916 22.2465 35.966 22.2465 33.4472C22.2465 30.9285 21.8944 28.816 21.1903 27.3535C20.4861 25.891 19.5111 25.1868 18.2923 25.1868H18.2653ZM56.1007 41.4368C50.8736 41.4368 46.6215 45.6889 46.6215 50.916C46.6215 56.1431 50.8736 60.3952 56.1007 60.3952C61.3277 60.3952 65.5798 56.1431 65.5798 50.916C65.5798 45.6889 61.3277 41.4368 56.1007 41.4368ZM54.7465 44.1452H57.4548V49.5618H62.8715V52.2702H54.7465V44.1452Z"
                    fill="#474CE6" />
                </svg>
                <h2>Correo</h2>
              </el-card>
            </a>
          </div>
          <div class="footer-icon">
            <a :href="'https://onedrive.live.com/'" target="_blank"
              @click="openLinkInNewTab('https://onedrive.live.com/')">
              <!--          <a href="https://drive.google.com" target="_blank" rel="noreferrer noopener">-->
              <el-card class="box-card d-flex justify-content-center pointer">
                <svg id="drive-icon" xmlns="http://www.w3.org/2000/svg" data-name="Layer 1" viewBox="0 0 96 66">
                  <g xmlns="http://www.w3.org/2000/svg" clip-path="url(#clip0_1734_2751)">
                    <path
                      d="M36.7188 18.8851L56.6688 31.6314L68.5438 26.3046C70.9484 25.1948 73.5609 24.6241 76.2031 24.6241C76.6484 24.6241 77.0641 24.6241 77.5094 24.6875C73.4422 7.69237 57.2625 -2.54909 41.3797 1.82652C34.225 3.79237 28.0203 8.51676 23.9531 15.1119C24.0422 15.1119 24.1609 15.1119 24.25 15.1119C28.6438 15.1119 32.9781 16.4119 36.7188 18.8851Z"
                      fill="#474CE6" />
                    <path
                      d="M36.7188 18.8532C32.9782 16.3801 28.6438 15.0801 24.2501 15.0801C24.161 15.0801 24.0423 15.0801 23.9532 15.0801C10.8313 15.2703 0.351655 26.7484 0.500093 40.763C0.559468 45.8362 2.04384 50.7825 4.77509 54.9679L22.3501 47.0728L30.1579 43.5532L47.5548 35.7215L56.6392 31.6313L36.6892 18.885L36.7188 18.8532Z"
                      fill="#474CE6" />
                    <path
                      d="M77.5094 24.6556C77.0641 24.6556 76.6485 24.5922 76.2032 24.5922C73.561 24.5922 70.9782 25.163 68.5438 26.2727L56.6688 31.5995L60.1126 33.8191L71.3938 41.0483L76.322 44.1873L93.1844 54.9678C98.2313 44.9483 94.7282 32.4556 85.3469 27.0337C82.9126 25.6386 80.2407 24.8142 77.5094 24.6239V24.6556Z"
                      fill="#474CE6" />
                    <path
                      d="M76.3517 44.2191L71.4235 41.0801L60.1423 33.8508L56.6985 31.6313L47.6142 35.7215L30.2173 43.5532L22.4095 47.0727L4.83447 54.9679C9.25791 61.7849 16.561 65.8435 24.3095 65.8435H76.2626C83.3579 65.8435 89.8892 61.6898 93.2438 54.9996L76.3813 44.2191H76.3517Z"
                      fill="#474CE6" />
                  </g>
                </svg>
                <!--              <img style="width: 25px; margin-right: 10px; margin-bottom: 5px;"
                                  :src="require(`@/assets/icons/inclined-paper-plane.svg`)"/>-->
                <h2>OneDrive</h2>
              </el-card>
            </a>
          </div>
          <div class="footer-icon">
            <a :href="'https://teams.microsoft.com/'" target="_blank"
              @click="openLinkInNewTab('https://teams.microsoft.com/')">
              <!--          <a href="https://undanet.slack.com" target="_blank" rel="noreferrer noopener">-->
              <el-card class="box-card d-flex justify-content-center pointer">
                <svg id="teams-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 68 66">
                  <g xmlns="http://www.w3.org/2000/svg" clip-path="url(#clip0_1734_2742)">
                    <path
                      d="M61.5453 15.4368C58.409 15.4368 55.8578 17.988 55.8578 21.1243C55.8578 24.2605 58.409 26.8118 61.5453 26.8118C64.6815 26.8118 67.2328 24.2605 67.2328 21.1243C67.2328 17.988 64.6815 15.4368 61.5453 15.4368ZM0.607788 59.3118L37.9828 65.8118V0.811768L0.607788 7.31177V59.3118Z"
                      fill="#474CE6" />
                    <path
                      d="M28.2328 20.7505V25.1868L21.749 25.4793L21.7165 44.7518L16.874 44.6056V25.658L10.3578 25.918V21.8393L28.2328 20.7505Z"
                      fill="white" />
                    <path
                      d="M52.6078 17.0618C52.6078 20.653 49.699 23.5618 46.1078 23.5618C44.1578 23.5618 42.419 22.7005 41.2328 21.3518V12.7718C42.419 11.423 44.1578 10.5618 46.1078 10.5618C49.699 10.5618 52.6078 13.4705 52.6078 17.0618ZM55.8578 31.6868V49.5618H61.5453C64.4053 49.5618 66.7615 47.433 67.1515 44.6868H67.2328V31.6868H55.8578ZM41.2328 26.8118V54.4368H46.9203C49.7803 54.4368 52.1365 52.308 52.5265 49.5618H52.6078V26.8118H41.2328Z"
                      fill="#474CE6" />
                  </g>
                </svg>
                <h2>Teams</h2>
              </el-card>
            </a>
          </div>
          <div class="footer-icon">
            <a :href="'https://grupoundanetaz.sharepoint.com/sites/GrupoUndanetAZ77/Shared%20Documents/Forms/AllItems.aspx?CT=1747820873638&OR=OWA%2DNT%2DMail&CID=036c856b%2Da9f6%2D8195%2D7eaa%2D546321e7381e&id=%2Fsites%2FGrupoUndanetAZ77%2FShared%20Documents%2FGeneral&viewid=1db04a99%2D0418%2D4399%2Db407%2D0ab8d6272447&ga=1'"
              target="_blank"
              @click="openLinkInNewTab('https://grupoundanetaz.sharepoint.com/sites/GrupoUndanetAZ77/Shared%20Documents/Forms/AllItems.aspx?CT=1747820873638&OR=OWA%2DNT%2DMail&CID=036c856b%2Da9f6%2D8195%2D7eaa%2D546321e7381e&id=%2Fsites%2FGrupoUndanetAZ77%2FShared%20Documents%2FGeneral&viewid=1db04a99%2D0418%2D4399%2Db407%2D0ab8d6272447&ga=1')">
              <!--          <a href="https://web.skype.com/" target="_blank" rel="noreferrer noopener">-->
              <el-card class="box-card d-flex justify-content-center pointer">
                <svg id="document-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66 66">
                  <path
                    d="M38.0797 0.811768C37.9887 0.811768 37.8977 0.837768 37.7937 0.850768L1.3937 7.71477C0.782698 7.83177 0.340698 8.39077 0.340698 9.01477V57.4788C0.340698 58.1028 0.782698 58.6618 1.3937 58.7788L37.7937 65.7728C37.8717 65.7858 37.9497 65.8118 38.0407 65.8118C38.3397 65.8118 38.6257 65.7208 38.8467 65.5258C39.1457 65.2788 39.3407 64.9018 39.3407 64.5118V2.11177C39.3407 1.72177 39.1587 1.34477 38.8467 1.09777C38.6257 0.915768 38.3527 0.824768 38.0797 0.811768ZM41.9407 11.3288V26.6818C44.9307 26.2008 47.2967 24.0558 48.1157 21.1958C50.6117 22.2358 52.7047 24.0038 54.1737 26.2788C51.5607 27.5398 49.7407 30.2178 49.7407 33.3118C49.7407 36.4058 51.5477 39.0318 54.1347 40.3058C52.6657 42.5418 50.5727 44.2708 48.0767 45.2978C47.2187 42.5028 44.8917 40.4098 41.9407 39.9418V55.2948C45.5677 54.7098 48.2457 51.7068 48.4017 47.9368C51.9117 46.6628 54.7977 44.2708 56.6957 41.0728C56.9687 41.0988 57.2677 41.1118 57.5537 41.1118C61.8567 41.1118 65.3537 37.6148 65.3537 33.3118C65.3537 29.0088 61.8567 25.5118 57.5537 25.5118C57.2937 25.5118 57.0337 25.5248 56.7867 25.5508C54.9017 22.2878 51.9767 19.8568 48.4147 18.5568C48.2067 14.8388 45.5417 11.9008 41.9537 11.3288H41.9407ZM19.3077 20.8448C20.6077 20.8448 21.6477 20.9618 22.5577 21.0918C23.4677 21.2218 24.3777 21.4818 25.1577 21.7418V26.8248C24.7677 26.5648 24.3777 26.3178 23.8577 26.0578C23.3377 25.7978 22.9477 25.6678 22.4407 25.5248C21.9337 25.3818 21.4137 25.2778 21.0237 25.2778C20.5037 25.1478 20.1137 25.1218 19.6067 25.1218C18.9567 25.1218 18.4237 25.1478 17.9037 25.2778C17.3837 25.4078 16.9937 25.5118 16.6037 25.7718C16.2137 26.0318 15.9667 26.3308 15.8367 26.5778C15.7067 26.8378 15.5507 27.2018 15.5507 27.5918C15.5507 27.9818 15.7067 28.3848 15.8367 28.6448C16.0967 28.9048 16.3437 29.2818 16.7337 29.5418C17.1237 29.8018 17.6437 30.0488 18.1507 30.3088C18.6707 30.5688 19.3337 30.8678 19.9837 31.1148C20.8937 31.5048 21.8037 31.8948 22.5837 32.4148C23.3637 32.8048 24.0137 33.3118 24.5337 33.9618C25.0537 34.4818 25.4437 35.1318 25.8337 35.9118C26.0937 36.6918 26.2367 37.4848 26.2367 38.3948C26.1067 39.8248 25.8597 40.8518 25.4697 41.7618C24.9497 42.6718 24.2607 43.4388 23.3637 43.9588C22.4667 44.4788 21.5697 44.8818 20.3997 45.1418C19.2297 45.4018 18.0467 45.5448 16.7467 45.5448C15.4467 45.5448 14.1337 45.4018 12.9637 45.1418C11.7937 45.0118 10.7797 44.6348 9.9997 44.2448V38.9278C10.9097 39.7078 11.9627 40.3188 13.1327 40.7218C14.3027 41.1118 15.3427 41.3718 16.4997 41.3718C17.1497 41.3718 17.8127 41.3718 18.3327 41.2548C18.8527 41.1248 19.2427 40.9818 19.6327 40.7218C20.0227 40.4618 20.2697 40.2148 20.3997 39.9548C20.5297 39.6948 20.6467 39.3178 20.6467 39.0578C20.6467 38.5378 20.5427 38.1478 20.2827 37.7578C20.0227 37.3678 19.6197 37.0948 19.2297 36.7048C18.7097 36.4448 18.1767 36.0678 17.5267 35.8078C16.8767 35.5478 16.2397 35.2618 15.4597 34.8718C13.6397 34.0918 12.2227 33.1948 11.3127 32.0248C10.4027 30.8548 9.8957 29.5678 9.8957 28.0078C9.8957 26.7078 10.1557 25.6678 10.6627 24.7578C11.1827 23.8478 11.8197 23.1718 12.7297 22.5218C13.6397 22.0018 14.5627 21.4818 15.7327 21.2218C16.9027 20.9618 18.0467 20.8578 19.3467 20.8578L19.3077 20.8448Z"
                    fill="#474CE6" />
                </svg>
                <h2>Documentos</h2>
              </el-card>
            </a>
          </div>
        </div>
      </div>

      <ImpersonationBanner v-if="userLogged && authenticated" />
      <UpdateBanner v-if="userLogged && authenticated" />
      <Navbar v-if="userLogged && authenticated" @toggleSidebar="toggleSidebar" />
      <AdminSidebar v-if="userLogged && authenticated" @close="showSidebar" @closeSide="toggleSidebar"
        :class="showSide ? 'side-open' : 'side-close'" />

    </div>
  </div>
</template>

<style lang="scss">
#app {
  font-family: $font-main;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: $primary-color;
  //height: 100vh;
  font-size: 1rem;
  background-color: #F3F0FB;

  &.loading {
    display: flex;
    align-items: center;
    justify-items: center;
  }

  .main-content {
    width: 100%;
    height: 100%;
    background-color: #F3F0FB;

    /*text-align: center;*/
    &.opened {
      padding: 80px 40px 20px 270px;
    }

    &.closed {
      padding: 80px 10px 20px 130px;
    }

    &.full-width {
      background-color: $bg-blue;
      padding: 0px;
      /*        padding-left: 0px;
              padding-top: 0px;*/
    }

  }

  @media (max-width:992px) {

    // Estado cerrado (fuera de la pantalla)
    .side-close {
      transform: translateX(-100%);
    }

    // Estado abierto (visible)
    .side-open {
      transform: translateX(0);
    }


    .overlay-diffuse {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.4);
      /* negro semitransparente */
      z-index: 100;
      /* asegúrate que esté sobre el main y bajo el sidebar si es necesario */
    }

    .main-content.openSide {
      filter: blur(4px);
      transition: filter 0.6s ease;
      // overflow: hidden;
      position: relative;
    }

    #footer.openSide {
      filter: blur(4px);
      transition: filter 0.6s ease;
    }

    .main-content.closSide,
    .main-content.full-width {
      filter: none;
      transition: filter 0.6s ease;
    }
  }
}
</style>
